import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import classNames from "classnames";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectKnownOutfits } from "../../app/slices/MetadataSlice";
import PiChip from "../../components/PiChip";
import PiPreviewStage from "../../components/PiPreviewStage";
import { Actor } from "../../models/Actor";
import {
  initialPreviewGender,
  OutfitPickerContext,
  outfitItem,
  specialItem,
} from "./outfitPickerModel";
import OutfitPickerDialog from "./OutfitPickerDialog";
import OutfitStage from "./OutfitStage";

/**
 * Shows an actor's outfit and opens the outfit picker.
 * - `stage`: a preview stage with a name bar that fills its column, beside the
 *   animation stage (take settings, Appearance tab).
 * - `compact`: a small field with a thumbnail (Set Outfit sequence node).
 * - `chip`: the interaction-popup chip, beside an actor chip (Set Outfit popup).
 */
export default function OutfitPickerField(props: {
  context: OutfitPickerContext;
  actor?: Actor;
  value: string;
  onChange: (value: string) => void;
  variant?: "compact" | "stage" | "chip";
  /** Chip only: a row of rowStart + the chip, with the outfit render below at the row's width. */
  withStage?: boolean;
  /** Chip with stage only: what comes before the chip in its row (actor chip, connector). */
  rowStart?: ReactNode;
  disabled?: boolean;
  className?: string;
}) {
  const { context, actor, value, onChange, variant = "compact", withStage, rowStart, disabled, className } = props;
  const { t } = useTranslation();
  const knownOutfits = useSelector(selectKnownOutfits);
  const [open, setOpen] = useState(false);

  // Default (take settings) keeps the actor's own outfit from their preset.
  const ownOutfit = context === "take" && actor?.outfit ? knownOutfits[actor.outfit] : undefined;
  const item = value
    ? outfitItem(knownOutfits[value] ?? { name: value }, "field")
    : specialItem(context);
  const gender = initialPreviewGender(actor?.gender);

  const name = String(
    item?.kind === "outfit"
      ? item.outfit.name
      : item?.kind === "default"
        ? t("outfit_picker.default")
        : item?.kind === "random"
          ? t("outfit_picker.random")
          : t("outfit_picker.select_outfit")
  );
  const defaultHint = String(
    ownOutfit
      ? t("outfit_picker.default_meta", { name: ownOutfit.name })
      : t("outfit_picker.default_meta_none")
  );

  const dialog = (
    <OutfitPickerDialog
      open={open}
      context={context}
      actor={actor}
      value={value}
      ownOutfit={ownOutfit}
      onChoose={onChange}
      onClose={() => setOpen(false)}
    />
  );

  if (variant === "stage") {
    // Design Studio actor-settings-layout/d-appearance*: one of the two Appearance
    // columns. With no take outfit, show what the actor wears anyway: the preset
    // outfit at full strength ("from the preset"), or the random state.
    const inherited = !value;
    const shown = value ? item : ownOutfit ? outfitItem(ownOutfit, "field") : undefined;
    const isRandom = context === "take" ? !shown : item?.kind === "random";
    const fromPreset = String(t("outfit_picker.from_preset"));
    return (
      <div className={classNames("flex min-h-0 w-full flex-1 flex-col", className)} data-testid="outfit-picker-field">
        <PiPreviewStage
          preview={shown && !isRandom ? <OutfitStage item={shown} gender={gender} fill /> : undefined}
          empty={
            isRandom
              ? {
                  icon: <ShuffleIcon fontSize="inherit" />,
                  title: String(t("outfit_picker.random_outfit")),
                  body: String(t("outfit_picker.random_outfit_body")),
                }
              : { icon: <CheckroomOutlinedIcon fontSize="inherit" />, title: String(t("outfit_picker.no_outfit")) }
          }
          name={isRandom ? String(t("outfit_picker.random")) : shown?.kind === "outfit" ? shown.outfit.name : name}
          meta={context === "take" && inherited ? fromPreset : undefined}
          actionLabel={String(t(shown && !isRandom ? "outfit_picker.change_outfit" : "outfit_picker.choose_outfit"))}
          onAction={() => setOpen(true)}
          onClear={value ? () => onChange("") : undefined}
          clearLabel={String(t(context === "take" ? "outfit_picker.reset_to_default" : "outfit_picker.clear_outfit"))}
          disabled={disabled}
          testIds={{ stage: "outfit-stage-preview", action: "outfit-stage-change", clear: "outfit-stage-clear" }}
        />
        {dialog}
      </div>
    );
  }

  if (variant === "chip") {
    // The interaction-popup chip (PiChip shell), so it lines up with the actor
    // chip beside it. It opens a dialog, so it ends in a gallery icon instead
    // of the menu chevron. withStage lays out the row (rowStart, then the chip)
    // with the render below it at the same width; the render opens the picker
    // too (Design Studio screens/set-outfit/e4-below.html).
    const glyph = (
      <span
        className={classNames(
          "flex h-[28px] w-[28px] flex-none items-center justify-center rounded-full bg-interaction-primary-washed",
          item ? "text-interaction-primary" : "text-interaction-primary-faded"
        )}
      >
        {item?.kind === "random" ? <ShuffleIcon sx={{ fontSize: 16 }} /> : <CheckroomOutlinedIcon sx={{ fontSize: 16 }} />}
      </span>
    );
    const chip = (
      <PiChip
        data-testid="outfit-picker-field"
        aria-label={String(t("outfit_picker.field_label"))}
        title={String(t(item ? "outfit_picker.change_outfit" : "outfit_picker.select_outfit"))}
        label={name}
        icon={glyph}
        isPlaceholder={!item}
        isOpen={open}
        disabled={disabled}
        onClick={() => setOpen(true)}
        trailingIcon={<GridViewOutlinedIcon sx={{ fontSize: 16 }} />}
        className={className}
      />
    );
    if (!withStage) {
      return (
        <>
          {chip}
          {dialog}
        </>
      );
    }
    // The row on top, the render below it at the row's full width.
    return (
      <div className="inline-flex max-w-full flex-col gap-4" data-testid="outfit-chip-layout">
        <div className="flex items-center gap-3">
          {rowStart}
          {chip}
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          aria-label={String(t(item ? "outfit_picker.change_outfit" : "outfit_picker.select_outfit"))}
          title={String(t(item ? "outfit_picker.change_outfit" : "outfit_picker.select_outfit"))}
          className="relative h-[220px] w-full cursor-pointer overflow-hidden rounded-[8px] border-0 bg-[var(--pi-outfit-stage)] p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-interaction-primary disabled:cursor-default"
          data-testid="outfit-chip-stage"
        >
          {item ? (
            <OutfitStage item={item} gender={gender} ownOutfit={ownOutfit} fill />
          ) : (
            <span className="flex h-full flex-col items-center justify-center gap-[8px] text-[13px] font-medium text-interaction-primary-faded">
              <CheckroomOutlinedIcon sx={{ fontSize: 32 }} />
              {String(t("outfit_picker.no_outfit"))}
            </span>
          )}
        </button>
        {dialog}
      </div>
    );
  }

  const hint =
    item?.kind === "default"
      ? defaultHint
      : String(t(context === "take" ? "outfit_picker.field_hint_take" : "outfit_picker.field_label"));

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        data-testid="outfit-picker-field"
        onClick={() => setOpen(true)}
        className={classNames(
          "inline-flex min-w-0 max-w-full items-center gap-[12px] rounded-[6px] border border-solid border-creator-primary-faded bg-creator-surface py-[6px] pl-[6px] pr-[12px] text-left text-creator-primary",
          disabled ? "cursor-default opacity-50" : "cursor-pointer hover:border-creator-primary",
          className
        )}
      >
        {item ? (
          <OutfitStage
            item={item}
            gender={gender}
            ownOutfit={ownOutfit}
            compact
            className="w-[40px] flex-none rounded-[5px]"
          />
        ) : (
          <div className="outfitpicker-stage outfitpicker-stage--plain w-[40px] flex-none" />
        )}
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[13px] font-medium">{name}</span>
          <span className="truncate text-[12px] text-creator-text-sub">{hint}</span>
        </span>
        <KeyboardArrowDownIcon sx={{ fontSize: 18 }} className="flex-none" />
      </button>
      {dialog}
    </>
  );
}
